import React, { useCallback, useRef, useMemo, useState, } from 'react'
import classNames from 'classnames'
import { makeStyles, } from '@material-ui/styles'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import AddBoxOutlined from '@material-ui/icons/AddBoxOutlined'
import { setRef, } from '@material-ui/core/utils/reactHelpers'
import ClassNameType from '@shapr/react-shared/types/ClassName'
import Downshift from 'downshift'
import Popper from '@material-ui/core/Popper'
import Paper from '@material-ui/core/Paper'
import RootRef from '@material-ui/core/RootRef'

import ListItem from './ListItem'
import SuggestionItem from './SuggestionItem'
import CriteriaType from './types/Criteria'

const Criteria = props => {
  const { className, criteria, ...restProps } = props
  const classes = useStyles()

  const textFieldRef = useRef()
  const setTextFieldRef = useCallback(ref => {
    setRef(textFieldRef, ref)
  }, [])

  const inputRef = useRef()
  const setInputRef = useCallback(ref => {
    setRef(criteria.inputRef, ref)
    setRef(inputRef, ref)
  }, [])

  const handleInputValueChange = useCallback(
    value => {
      criteria.setInputValue(value)
    },
    [criteria.setInputValue,],
  )

  const stateReducer = useCallback(
    (state, changes) => {
      switch (changes.type) {
        default:
          return changes
      }
    },
    [criteria.isWithMultipleSelection,],
  )

  const [highlightedSuggestion, setHighlightedSuggestion,] = useState(undefined)

  const handleStateChange = useCallback(
    (changes, stateAndHelpers) => {
      if (criteria.isWithSuggestions && changes.hasOwnProperty('isOpen'))
        criteria.setSuggestionIsOpen(changes.isOpen)
      if (
        criteria.isWithSuggestions &&
        changes.hasOwnProperty('highlightedIndex')
      )
        setHighlightedSuggestion(changes.highlightedIndex)

      switch (changes.type) {
        case Downshift.stateChangeTypes.keyDownEnter:
          criteria.setInputValue('')
          break
        case Downshift.stateChangeTypes.keyDownEscape:
          criteria.setInputValue('')
          break
        case Downshift.stateChangeTypes.blurInput:
          if (criteria.isWithMultipleSelection)
            criteria.setInputValue(stateAndHelpers.inputValue)
          else criteria.setInputValue('')
          break
        case Downshift.stateChangeTypes.clickItem:
          if (criteria.isWithMultipleSelection) criteria.setInputValue('')
          else criteria.setInputValue(stateAndHelpers.inputValue)
          break
        case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem:
          if (criteria.isWithMultipleSelection) criteria.setInputValue('')
          break
        case Downshift.stateChangeTypes.mouseUp:
          if (!criteria.isWithMultipleSelection)
            criteria.setInputValue(stateAndHelpers.inputValue)
          break
        default:
          break
      }
    },
    [criteria.isWithSuggestions, criteria.isWithMultipleSelection,],
  )

  const handleSelectionAdd = useCallback(() => {
    criteria.onSelectChange({
      type: 'add',
    })
  }, [criteria.onSelectChange,])

  const handleSelectionRemove = useCallback(
    value => {
      criteria.onSelectChange({
        type: 'remove',
        value,
      })
    },
    [criteria.onSelectChange,],
  )

  const isAddDisabled = useMemo(
    () =>
      !criteria.inputValue ||
      (criteria.selectedValues &&
        criteria.selectedValues.includes(criteria.inputValue)),
    [criteria.selectedValues, criteria.inputValue,],
  )

  const handleOnChange = useCallback(
    value => {
      if (criteria.onSelectChange)
        criteria.onSelectChange({
          type: 'add',
          value,
        })
    },
    [criteria.onSelectChange,],
  )

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' && typeof highlightedSuggestion === 'undefined') {
        e.preventDefault()
        e.stopPropagation()
        handleSelectionAdd()
      }
    },
    [criteria.onSelectChange,],
  )

  const handleOnFocus = useCallback(e => {
    if (criteria.setSuggestionIsOpen) criteria.setSuggestionIsOpen(true)
  })

  return (
    <div className={classNames(classes.root, className)} {...restProps}>
      <Downshift
        onChange={handleOnChange}
        stateReducer={stateReducer}
        onStateChange={handleStateChange}
        inputValue={criteria.inputValue}
        onInputValueChange={handleInputValueChange}
        selectedItem={criteria.selectedValues}
        isOpen={criteria.suggestionIsOpen}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          inputValue,
          highlightedIndex,
          selectedItem,
        }) => (
          <div>
            <RootRef rootRef={setTextFieldRef}>
              <TextField
                label={criteria.label}
                margin="normal"
                variant="outlined"
                value={inputValue}
                inputRef={setInputRef}
                className={classes.textField}
                onFocus={handleOnFocus}
                onKeyDown={handleKeyDown}
                InputProps={{
                  ...getInputProps(),
                  endAdornment: criteria.isWithAddButton ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleSelectionAdd}
                        disabled={isAddDisabled}
                        aria-label={`Add a ${criteria.label}`}
                      >
                        <AddBoxOutlined />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    undefined
                  ),
                }}
              />
            </RootRef>
            <Popper
              open={
                isOpen &&
                !!criteria.suggestionValues &&
                !!criteria.suggestionValues.length
              }
              modifiers={{
                flip: {
                  enabled: false,
                },
              }}
              anchorEl={textFieldRef.current}
              className={classes.popper}
            >
              <div {...getMenuProps({}, { suppressRefError: true, })}>
                <Paper
                  square
                  className={classes.paper}
                  style={{
                    width: textFieldRef.current
                      ? textFieldRef.current.clientWidth
                      : null,
                  }}
                >
                  {!!criteria.suggestionValues &&
                    !!criteria.suggestionValues.length &&
                    criteria.suggestionValues
                      .filter(
                        e =>
                          !criteria.selectedValues ||
                          !criteria.selectedValues.includes(e),
                      )
                      .map((suggestion, index) => (
                        <SuggestionItem
                          suggestion={suggestion}
                          key={suggestion}
                          index={index}
                          itemProps={getItemProps({ item: suggestion, })}
                          highlightedIndex={highlightedIndex}
                          selectedItem={selectedItem}
                        />
                      ))}
                </Paper>
              </div>
            </Popper>
            {!!criteria.selectedValues &&
              criteria.selectedValues.map(item => (
                <ListItem
                  item={item}
                  onDelete={handleSelectionRemove}
                  className={classes.listItem}
                  key={item}
                />
              ))}
          </div>
        )}
      </Downshift>
    </div>
  )
}

const listItemHeight = 48
const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
  },
  textField: { width: '100%', },
  popper: { zIndex: 1, },
  paper: { marginTop: 8, maxHeight: listItemHeight * 5, overflowY: 'auto', },
  listItem: { padding: '0 13px', height: listItemHeight, },
}))

Criteria.propTypes = {
  className: ClassNameType,
  criteria: CriteriaType.isRequired,
}

export default React.memo(Criteria)
